import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ROUTES } from "@constants";
import images from "@assets/images";
import "./Toolbar.css";

const TOOLBAR_ITEMS = [
  { id: "music", icon: images.iconMusic, label: "Âm thanh" },
  {
    id: "fullscreen",
    icon: images.iconFullscreen,
    label: "Toàn màn hình",
  },
  {
    id: "zoom-in",
    icon: images.iconPlus,
    label: "Phóng to",
  },
  {
    id: "zoom-out",
    icon: images.iconMinus,
    label: "Thu nhỏ",
  },
];

function Toolbar() {
  const location = useLocation();
  const audioRef = useRef(null);
  const [activeLabelId, setActiveLabelId] = useState(null);
  const [musicEnabled, setMusicEnabled] = useState(true);
  const [isOpen, setIsOpen] = useState(false); // mobile: toolbar ẩn mặc định

  // Click ngoài toolbar → đóng label và đóng toolbar
  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (!e.target.closest(".toolbar__btn")) {
        setActiveLabelId(null);
      }
      if (!e.target.closest(".toolbar")) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, []);

  // Khởi tạo audio một lần duy nhất: volume + loop + tự phát (có fallback tương tác)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = 0.45;
    audio.loop = true;

    const tryPlay = () => {
      audio.play().catch(() => {
        // Trình duyệt chặn → đợi tương tác đầu tiên của người dùng
        const onInteract = () => {
          audio.play().catch(() => {});
          window.removeEventListener("pointerdown", onInteract);
          window.removeEventListener("touchstart", onInteract);
        };
        window.addEventListener("pointerdown", onInteract, { once: true });
        window.addEventListener("touchstart", onInteract, { once: true });
      });
    };

    tryPlay();
  }, []); // chỉ chạy 1 lần khi mount

  // Bật/tắt nhạc theo musicEnabled
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (musicEnabled) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, [musicEnabled]);

  // Chỉ hiển thị Toolbar ở Tổng quan (/), Mặt bằng (/mat-bang) và Tiện ích (/tien-ich)
  const allowedRoutes = [ROUTES.HOME, ROUTES.MAT_BANG, ROUTES.TIEN_ICH];
  if (!allowedRoutes.includes(location.pathname)) {
    return null;
  }

  const handleAction = (id, e) => {
    e.stopPropagation(); // tránh đóng label ngay lập tức từ document click

    setActiveLabelId((prev) => (prev === id ? null : id));

    if (id === "music") {
      setMusicEnabled((prev) => !prev);
      return;
    }

    if (id === "fullscreen") {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.error(`Error attempting to enable fullscreen: ${err.message}`);
        });
      } else {
        document.exitFullscreen();
      }
    }
  };

  const isMatBang = location.pathname === ROUTES.MAT_BANG;

  return (
    <aside
      className={`toolbar${isMatBang ? " toolbar--collapsible" : ""}${isMatBang && isOpen ? " toolbar--open" : ""}`}
      aria-label="Thanh công cụ"
    >
      <audio
        ref={audioRef}
        src={`${import.meta.env.BASE_URL}musics/Soft Snowy Jazz Loop.wav`}
      />

      <ul className="toolbar__list" role="list">
        {/* Nút toggle — chỉ hiện trên mobile và chỉ ở trang mặt bằng */}
        {isMatBang && (
          <li className="toolbar__item toolbar__item--toggle">
            <button
              className="toolbar__btn toolbar__btn--toggle"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen((v) => !v);
              }}
              type="button"
              aria-label={isOpen ? "Thu gọn công cụ" : "Mở công cụ"}
              aria-expanded={isOpen}
            >
              <svg
                className="toolbar__chevron"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                {isOpen ? (
                  <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                ) : (
                  <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                )}
              </svg>
            </button>
          </li>
        )}

        {TOOLBAR_ITEMS.map(({ id, icon, label }) => (
          <li key={id} className="toolbar__item">
            <button
              id={`toolbar-btn-${id}`}
              className={`toolbar__btn${id === "music" && !musicEnabled ? " toolbar__btn--music-off" : ""}${activeLabelId === id ? " toolbar__btn--show-label" : ""}`}
              aria-label={label}
              title={label}
              onClick={(e) => handleAction(id, e)}
              type="button"
            >
              <span className="toolbar__label" aria-hidden="true">
                {label}
              </span>
              <img src={icon} alt={label} className="toolbar__icon" />
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}

export default Toolbar;
